import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { TeamAssistantInstallationProvider } from '@dreamai/types';

export const PROVIDER_LABELS: Record<
  TeamAssistantInstallationProvider,
  string
> = {
  slack: 'Slack',
  teams: 'Microsoft Teams',
};

/** Query params the backend's Slack OAuth callback appends to the console URL. */
export const INSTALL_RESULT_PARAM = 'slack';
export const INSTALL_REASON_PARAM = 'reason';

export type InstallReturn = { type: 'success' | 'error'; message: string };

const REASON_MESSAGES: Record<string, string> = {
  state:
    'The link back from Slack was not valid or had expired. Please try again.',
  missing_code: 'Slack did not complete the installation. Please try again.',
  not_configured:
    'Slack is not set up on this server yet. Please contact support.',
  team_assistant:
    'This Team Secretary no longer exists or has been archived, so it cannot be connected.',
  already_installed:
    'This Team Secretary is already connected to a workspace. Disconnect it first to switch.',
  workspace_taken:
    'That Slack workspace is already connected to another Team Secretary.',
  access_denied: 'The installation was cancelled in Slack.',
  invalid_code:
    'Slack rejected the installation because the link had already been used. Please try again.',
  internal: 'Something went wrong on our side. Please try again.',
};

export function installReturnFeedback(
  outcome: string,
  reason: string | null,
): InstallReturn {
  if (outcome === 'ok') {
    return { type: 'success', message: 'Connected to Slack.' };
  }
  const detail =
    (reason && REASON_MESSAGES[reason]) ??
    (reason
      ? `Slack reported a problem (${reason.replace(/_/g, ' ')}). Please try again.`
      : 'Please try again.');
  return { type: 'error', message: `Could not connect to Slack. ${detail}` };
}

/**
 * Reads the result of a Slack installation round-trip from the URL exactly
 * once and strips it, so a reload does not repeat the message.
 */
export function useInstallReturn(): InstallReturn | null {
  const [searchParams, setSearchParams] = useSearchParams();
  const [result, setResult] = useState<InstallReturn | null>(null);

  useEffect(() => {
    const outcome = searchParams.get(INSTALL_RESULT_PARAM);
    if (!outcome) return;
    setResult(
      installReturnFeedback(outcome, searchParams.get(INSTALL_REASON_PARAM)),
    );
    const next = new URLSearchParams(searchParams);
    next.delete(INSTALL_RESULT_PARAM);
    next.delete(INSTALL_REASON_PARAM);
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  return result;
}
